import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home", testid: "nav-link-home" },
  { to: "/services", label: "Services", testid: "nav-link-services" },
  { to: "/careers", label: "Careers", testid: "nav-link-careers" },
  { to: "/consulting", label: "Consulting", testid: "nav-link-consulting" },
  { to: "/contact", label: "Contact", testid: "nav-link-contact" },
];

export const Wordmark = ({ className = "h-16" }) => (
  <img
    src="/logo.png"
    alt="New Beginnings Healthcare Services, LLC — Exceptional People, Extraordinary Care"
    data-testid="nav-logo-wordmark"
    className={`w-auto ${className}`}
  />
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? "border-b border-blush-200 bg-white/90 shadow-[0_8px_30px_rgba(31,41,55,0.06)] backdrop-blur-xl"
          : "border-b border-transparent bg-white/70 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/" aria-label="New Beginnings Healthcare Services home">
          <Wordmark />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `group relative text-sm font-medium tracking-wide transition-colors duration-300 ${
                  isActive ? "text-gold-deep" : "text-charcoal-light hover:text-gold-deep"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute -bottom-1.5 left-0 h-px bg-gold transition-all duration-300 ${
                      isActive ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <Link
            to="/request-care"
            data-testid="nav-button-request-care"
            className="btn-pink !px-6 !py-2.5 !text-xs"
          >
            Request Care
          </Link>
          <Link
            to="/careers"
            data-testid="nav-button-apply-now"
            className="btn-gold-outline !px-6 !py-2.5 !text-xs"
          >
            Apply Now
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="rounded-full border border-blush-300 p-2.5 text-charcoal lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Toggle navigation menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="border-t border-blush-200 bg-white/95 px-6 pb-8 pt-4 backdrop-blur-xl lg:hidden"
        >
          <nav className="flex flex-col gap-1" aria-label="Mobile">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-${l.testid}`}
                className={({ isActive }) =>
                  `rounded-xl px-4 py-3 text-base font-medium transition-colors ${
                    isActive
                      ? "bg-blush-50 text-gold-deep"
                      : "text-charcoal-light hover:bg-blush-50 hover:text-gold-deep"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-4 flex flex-col gap-3">
            <Link to="/request-care" data-testid="mobile-nav-button-request-care" className="btn-pink">
              Request Care
            </Link>
            <Link to="/careers" data-testid="mobile-nav-button-apply-now" className="btn-gold-outline">
              Apply Now
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
